import { useState } from 'react';
import { Checkbox, type CheckboxProps, Popover, Icon } from '@ets/design-system';

import { usePermissions } from '_common/components/Permissions/PermissionsContext';
import PermissionPopover from './PermissionPopover/PermissionPopover';
import { getModalContainer } from 'utils';

type PermissionColumnProps = {
  objectId: ObjectId;
  permission: Permission.Collaborator['permissions'][keyof Permission.Collaborator['permissions']];
  collaborator: Permission.Collaborator;
  recursive: boolean;
  onChange: (
    code: Permission.CollaboratorPermission,
    collaboratorId: Permission.Collaborator['collaboratorId'],
    collaboratorType: Permission.Collaborator['collaboratorType'],
  ) => void;
};

const PermissionColumn = ({
  permission,
  objectId,
  collaborator,
  recursive,
  onChange,
}: PermissionColumnProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { canEditPermissions, canEditPermission } = usePermissions();

  const { collaboratorId, collaboratorType } = collaborator;

  const handleChange: CheckboxProps['onChange'] = () => {
    if (!permission.locked) {
      onChange(permission.code, collaboratorId, collaboratorType);
    } else {
      if (isOpen) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    }
  };

  return (
    <Popover.Root placement="bottom-start" open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
      <div style={{ display: 'flex', justifyContent: 'left', position: 'relative', zIndex: 0 }}>
        <Popover.Trigger>
          {canEditPermissions ? (
            <Checkbox
              size="xs"
              checked={permission.locked ? 'indeterminate' : permission.active ? true : false}
              disabled={
                permission.disabled ||
                (!permission.locked &&
                  !canEditPermission(collaborator.collaboratorId, permission.active))
              }
              onChange={(value) => {
                handleChange(value);
              }}
              testId={`collaborator-${collaboratorId}-${permission.code}`}
            />
          ) : (
            <Icon
              icon={permission.active || permission.locked ? 'circle-check' : 'circle-xmark'}
              size="4"
              color={permission.active || permission.locked ? 'success' : 'error'}
              testId={`collaborator-${collaboratorId}-${permission.code}-${
                permission.active || permission.locked ? 'checked' : 'unchecked'
              }`}
            />
          )}
        </Popover.Trigger>
        <Popover.Content
          container={getModalContainer() ?? document.body}
          testId={`collaborator-${collaboratorId}-${permission.code}`}
        >
          <PermissionPopover
            objectId={objectId}
            permission={permission.code}
            collaboratorType={collaboratorType}
            collaboratorId={collaboratorId}
            recursive={recursive}
          />
        </Popover.Content>
      </div>
    </Popover.Root>
  );
};

export default PermissionColumn;
